
import React, { PureComponent } from "react";
import ReactDOM from "react-dom";
import { Button, Panel } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SmoothCollapse from "react-smooth-collapse";
import _ from "lodash";
import Glyphicon from "../app-icon";
import ErrorBoundary from "../errors/errorBoundary";
import {
	PROPERTY_PANEL_EDITOR_CLASS,
	PROPERTY_PANEL_EXPANSION_EVENT,
	PROPERTY_PANEL_FILTER_EVENT,
	PROPERTY_PANEL_FILTER_STATE_KEY,
	isPropertyPanelKeyVisibleForFilter,
	normalizePanelKey
} from "../../utils/propertyPanelUtils";

const PROPERTY_PANEL_TITLE_ICONS = {
	"3d split screen": "cube",
	"activity automation": "bolt",
	"activity automation json": "code",
	"activity behavior": "tachometer-alt",
	"activity information": "info-circle",
	"activity summary panels & actions": "tasks",
	"advanced": "cogs",
	"advanced options": "cogs",
	"ai action": "user-robot",
	"ai assistant (alpha test)": "user-robot",
	"attached data sources": "database",
	"audio options": "volume-up",
	"author properties": "code",
	"background audio": "music",
	"barcode scanner options": "barcode",
	"basic options": "sliders-h",
	"breadcrumb bar": "route",
	"choice layout": "border-all",
	"comments": "comment-alt",
	"comments audio file": "file-audio",
	"completion status": "check-circle",
	"container header": "heading",
	"container layout": "border-all",
	"content model references": "project-diagram",
	"date picker options": "calendar-alt",
	"default text alignment": "align-justify",
	"description": "file-alt",
	"drive root path": "folder-tree",
	"filter selector": "filter",
	"filters": "filter",
	"classes": "code",
	"fonts": "font",
	"functions": "code",
	"general": "cogs",
	"icon": "icons",
	"id": "fingerprint",
	"id & groups": "tags",
	"image options": "image",
	"item layout": "ruler-combined",
	"item navigation": "route",
	"item tools": "wrench",
	"keyboard settings": "keyboard",
	"label 1": "heading",
	"label 1 advanced": "cogs",
	"label 1 audio file": "file-audio",
	"label 2": "heading",
	"label 2 advanced": "cogs",
	"label 2 audio file": "file-audio",
	"labels overlay": "layer-group",
	"link": "link",
	"linking options": "link",
	"logo image (for normal/large screens)": "image",
	"logo image (for small screens)": "image",
	"media": "photo-video",
	"media controls": "sliders-h",
	"media upload options": "cloud-upload",
	"multi choice items": "list-ul",
	"multi choice itens": "list-ul",
	"navigation title": "route",
	"on complete": "flag-checkered",
	"participant completion": "user-check",
	"pdf options": "file-pdf",
	"picker options": "list-alt",
	"preferences": "sliders-h",
	"presentation mode": "desktop",
	"preview params": "search",
	"print options": "print",
	"ranking options": "list-ol",
	"reference": "link",
	"related items": "link",
	"single choice items": "list-ul",
	"single choice itens": "list-ul",
	"sketchpad options": "draw-square",
	"split screen pricing content": "columns",
	"styling": "palette",
	"styles": "palette",
	"stylesheet": "file-code",
	"submit button": "paper-plane",
	"text alignment": "align-justify",
	"text input options": "font",
	"theme": "palette",
	"title": "heading",
	"title audio file": "file-audio",
	"title advanced": "cogs",
	"title card image": "image",
	"timers": "stopwatch",
	"tracking": "map-marked-alt",
	"value trigger": "bolt",
	"video options": "video",
	"visibility": "eye",
	"workflow": "project-diagram",
	"yes/no options": "check-square"
};

const titleIconStyles = {
	title: {
		display: "inline-flex",
		alignItems: "center",
		gap: "8px",
		minWidth: 0
	},
	icon: {
		flex: "0 0 auto",
		opacity: 0.84
	}
};

export function getPropertyPanelTitleIcon(titleText) {
	const normalizedTitle = _.toString(titleText || "").trim().toLowerCase();
	return PROPERTY_PANEL_TITLE_ICONS[normalizedTitle] || null;
}

export function renderPropertyPanelTitleWithIcon(titleText, titleIcon = null) {
	const icon = titleIcon || getPropertyPanelTitleIcon(titleText);
	if (!icon) {
		return titleText;
	}
	return (
		<span style={titleIconStyles.title}>
			<FontAwesomeIcon icon={icon} fixedWidth style={titleIconStyles.icon} />
			<span>{titleText}</span>
		</span>
	);
}

export class AmbifiBasicPanel extends PureComponent {
	constructor(props) {
		super(props);

		let parentExpanded = true;
		if (this.props.hasOwnProperty("collapsed") && this.props.collapsed) {
			parentExpanded = false;
		}

		this.state = {
			expanded: parentExpanded,
			hasEverExpanded: parentExpanded,
			forceCollapsible: false,
			externallyHidden: false,
			propertyPanelDepth: 0
		};

		this.panelRef = null;
		this.handleToggle = this.handleToggle.bind(this);
		this.handlePropertyPanelExpansionEvent = this.handlePropertyPanelExpansionEvent.bind(this);
		this.handlePropertyPanelFilterEvent = this.handlePropertyPanelFilterEvent.bind(this);
	}

	componentDidMount() {
		if (typeof window !== "undefined") {
			window.addEventListener(PROPERTY_PANEL_EXPANSION_EVENT, this.handlePropertyPanelExpansionEvent);
			window.addEventListener(PROPERTY_PANEL_FILTER_EVENT, this.handlePropertyPanelFilterEvent);
			window.setTimeout(() => {
				this.updateForceCollapsibleFromContext();
				this.applyCurrentPropertyPanelFilter();
			}, 0);
		}
	}

	componentWillUnmount() {
		if (typeof window !== "undefined") {
			window.removeEventListener(PROPERTY_PANEL_EXPANSION_EVENT, this.handlePropertyPanelExpansionEvent);
			window.removeEventListener(PROPERTY_PANEL_FILTER_EVENT, this.handlePropertyPanelFilterEvent);
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (nextProps.collapsed !== this.props.collapsed) {
			this.setState({
				expanded: !nextProps.collapsed,
				hasEverExpanded: this.state.hasEverExpanded || !nextProps.collapsed
			});
		} else if (!nextProps.collapsed && nextProps.expandToken && nextProps.expandToken !== this.props.expandToken) {
			this.setState({
				expanded: true,
				hasEverExpanded: true
			});
		}
	}

	getPanelNode() {
		if (!this.panelRef) {
			return null;
		}
		try {
			return ReactDOM.findDOMNode(this.panelRef);
		} catch (err) {
			return null;
		}
	}

	getPropertyPanelRoot() {
		const node = this.getPanelNode();
		if (!node || !node.closest) {
			return null;
		}
		return node.closest(`.${PROPERTY_PANEL_EDITOR_CLASS}`);
	}

	getPropertyPanelKeys() {
		return [
			this.props.anchor,
			this.props.id,
			this.props.titleText,
			this.props.className
		]
			.filter((value) => !_.isEmpty(_.toString(value || "")))
			.map(normalizePanelKey)
			.filter(Boolean);
	}

	getPropertyPanelDepth() {
		const node = this.getPanelNode();
		const root = this.getPropertyPanelRoot();
		if (!node || !root) {
			return 0;
		}

		let depth = 0;
		let current = node.parentElement;
		while (current && current !== root) {
			if (
				current.classList &&
				current.classList.contains("ambifi-panel") &&
				current.classList.contains("basic")
			) {
				depth += 1;
			}
			current = current.parentElement;
		}
		return depth;
	}

	updateForceCollapsibleFromContext() {
		const propertyPanelRoot = this.getPropertyPanelRoot();
		const forceCollapsible = !!propertyPanelRoot && !this.props.onTitleClick;
		const propertyPanelDepth = propertyPanelRoot ? this.getPropertyPanelDepth() : 0;
		if (
			forceCollapsible !== this.state.forceCollapsible ||
			propertyPanelDepth !== this.state.propertyPanelDepth
		) {
			this.setState({ forceCollapsible, propertyPanelDepth });
		}
	}

	applyCurrentPropertyPanelFilter() {
		if (typeof window === "undefined") {
			return;
		}
		const detail = window[PROPERTY_PANEL_FILTER_STATE_KEY];
		if (detail) {
			this.handlePropertyPanelFilterEvent({ detail });
		}
	}

	handlePropertyPanelExpansionEvent(e) {
		if (!this.getPropertyPanelRoot()) {
			return;
		}

		const effectiveCollapsible = this.props.collapsible || this.state.forceCollapsible;
		if (!effectiveCollapsible) {
			return;
		}

		const targetAnchor = _.get(e, "detail.anchor", null);
		if (targetAnchor) {
			const targetKey = normalizePanelKey(targetAnchor);
			const panelKeys = this.getPropertyPanelKeys();
			if (!targetKey || !panelKeys.some((key) => key === targetKey)) {
				return;
			}
		}

		const expanded = !!_.get(e, "detail.expanded", false);
		let expansionChanged = false;
		this.setState((prevState) => {
			if (prevState.expanded === expanded) {
				return null;
			}
			expansionChanged = true;
			return {
				expanded,
				hasEverExpanded: prevState.hasEverExpanded || expanded
			};
		}, () => {
			if (expansionChanged && this.props.unmountOnCollapse) {
				this.notifyToggle(expanded, e);
			}
		});
	}

	handlePropertyPanelFilterEvent(e) {
		if (!this.getPropertyPanelRoot()) {
			return;
		}

		const panelKeys = this.getPropertyPanelKeys();
		const externallyHidden = panelKeys.length > 0 && !isPropertyPanelKeyVisibleForFilter(panelKeys, _.get(e, "detail", {}));

		if (externallyHidden !== this.state.externallyHidden) {
			this.setState({ externallyHidden });
		}
	}

	notifyToggle(expanded, e) {
		if (this.props.onToggle) {
			this.props.onToggle(expanded, e);
		}
		if (expanded && this.props.onExpand) {
			this.props.onExpand(e);
		}
		if (!expanded && this.props.onCollapse) {
			this.props.onCollapse(e);
		}
	}

	handleToggle(e) {
		const nextExpanded = !this.state.expanded;
		this.setState((prevState) => ({
			expanded: nextExpanded,
			hasEverExpanded: prevState.hasEverExpanded || nextExpanded
		}));

		this.notifyToggle(nextExpanded, e);
	}

	render() {
		const { titleText, titleIcon, headerContent, children, collapsible = false, className = null, subTitle = null, anchor = null, id = null, onTitleClick = null, compact = false, borderless = false, lazyMount = false, unmountOnCollapse = false } = this.props;
		const effectiveCollapsible = collapsible || this.state.forceCollapsible;
		const isPropertyPanel = this.state.forceCollapsible;
		const isNestedPropertyPanel = isPropertyPanel && this.state.propertyPanelDepth > 0;
		const resolvedTitleIcon = titleIcon || (isPropertyPanel && !isNestedPropertyPanel ? getPropertyPanelTitleIcon(titleText) : null);
		const titleComponent = resolvedTitleIcon ? renderPropertyPanelTitleWithIcon(titleText, resolvedTitleIcon) : titleText;
		let headerComponent;
		let _headerContent = headerContent;
		const headerStyle = Object.assign(
			{},
			compact ? styles.headerCompact : styles.header,
			isPropertyPanel ? styles.propertyPanelPlainHeader : null,
			isNestedPropertyPanel ? styles.propertyPanelNestedPlainHeader : null
		);
		const bodyStyle = Object.assign(
			{},
			compact ? styles.bodyCompact : styles.body,
			isPropertyPanel ? styles.propertyPanelBody : null,
			isNestedPropertyPanel ? styles.propertyPanelNestedBody : null
		);
		const panelStyle = Object.assign(
			{},
			isPropertyPanel ? styles.propertyPanel : null,
			isNestedPropertyPanel ? styles.propertyPanelNested : null,
			borderless ? { border: "none", boxShadow: "none" } : {},
			this.state.externallyHidden ? styles.propertyPanelHidden : {}
		);
		const headerWrapperStyle = Object.assign(
			{},
			isPropertyPanel ? styles.propertyPanelHeading : null,
			isNestedPropertyPanel ? styles.propertyPanelNestedHeading : null,
			borderless ? { borderBottom: "none" } : {}
		);
		if (!_headerContent) {
			let textComponent = titleComponent;
			_headerContent = (
				<div className="row" style={headerStyle}>
					<div className="col-xs-12 vcenter">
						{textComponent}

					</div>
					{subTitle && !_.isEmpty(subTitle) && <div className="col-xs-12 vcenter"><small>{subTitle}</small></div>}
				</div>
			);
		}

		if (effectiveCollapsible) {

			const addtlHeaderProps = {};

			if (id) {
				addtlHeaderProps.id = id;
			}

			let chevronDown = <span><Glyphicon glyph="chevron-down" /></span>;
			let chevronUp = <span><Glyphicon glyph="chevron-up" /></span>;
			_headerContent = (
				<div className="row-eq-height disable-select" style={Object.assign({}, styles.collapsible, isPropertyPanel ? styles.propertyPanelCollapsible : null, isNestedPropertyPanel ? styles.propertyPanelNestedCollapsible : null, compact ? styles.headerCompact : null)} onClick={this.handleToggle} {...addtlHeaderProps}>
					<div className="col-xs-8 vcenter">
						{headerContent ? _headerContent : titleComponent}
						{subTitle && !_.isEmpty(subTitle) && <div><small>{subTitle}</small></div>}
					</div>
					<div className="col-xs-4 vcenter" style={styles.collapsibleButtonContainer}>
						<Button /*onClick={() => this.setState({ expanded: !this.state.expanded })}*/ bsSize="small" style={isPropertyPanel ? styles.propertyPanelToggleButton : null}>{this.state.expanded ? chevronUp : chevronDown}</Button>
					</div>
				</div>
			);
		}


		headerComponent = (
			<Panel.Heading className="header" style={Object.assign({}, onTitleClick ? { cursor: "pointer" } : {}, headerWrapperStyle)}>
				<div onClick={onTitleClick}>
					{_headerContent}
				</div>
			</Panel.Heading>
		);


		const shouldRenderBody = unmountOnCollapse && effectiveCollapsible
			? this.state.expanded
			: !lazyMount || !effectiveCollapsible || this.state.expanded || this.state.hasEverExpanded;
		let body = shouldRenderBody ? <Panel.Body className="body" style={bodyStyle}>
			{children}
		</Panel.Body> : null;
		if (effectiveCollapsible) {
			body = <SmoothCollapse allowOverflowWhenOpen={true} className="body-collapse" expanded={this.state.expanded}>{body}</SmoothCollapse>;
		}

		let additionalProps = {
			className: "ambifi-panel basic "
		};
		if (className) {
			additionalProps.className += className;
		}
		if (isPropertyPanel) {
			additionalProps.className += " property-panel ";
		}
		if (isNestedPropertyPanel) {
			additionalProps.className += " property-panel-nested ";
		}

		if (anchor) {
			additionalProps.id = anchor;
		} else if (this.state.forceCollapsible && titleText) {
			additionalProps.id = normalizePanelKey(titleText);
		}

		if (isPropertyPanel) {
			const propertyPanelTitleText = _.toString(titleText || "");
			if (propertyPanelTitleText) {
				additionalProps["data-property-panel-title"] = propertyPanelTitleText;
			}
			if (additionalProps.id) {
				additionalProps["data-property-panel-anchor"] = additionalProps.id;
			}
			additionalProps["data-property-panel-depth"] = this.state.propertyPanelDepth;
		}

		return (
			<Panel  {...additionalProps} style={panelStyle} ref={(panel) => { this.panelRef = panel; }}>
				<ErrorBoundary>
					{headerComponent}
					{body}
				</ErrorBoundary>
			</Panel>
		);
	}
}

const styles = {
	body: { paddingLeft: "0.5em", paddingRight: 0 },
	bodyCompact: { padding: "6px 8px" },
	collapsible: { fontWeight: "bold", verticalAlign: "middle", cursor: "pointer" },
	collapsibleButtonContainer: { justifyContent: "flex-end" },
	header: { fontWeight: "bold" },
	headerCompact: { fontWeight: "bold", padding: "6px 8px" },
	propertyPanel: {
		background: "#ffffff",
		backgroundColor: "#ffffff",
		border: "1px solid #cbd7e5",
		borderRadius: "6px",
		boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
		margin: "0 0 8px 0",
		overflow: "visible"
	},
	propertyPanelNested: {
		background: "#ffffff",
		backgroundColor: "#ffffff",
		border: "1px solid #d8e2ee",
		borderRadius: "4px",
		boxShadow: "none",
		margin: "8px 0"
	},
	propertyPanelHidden: {
		display: "none",
		margin: 0
	},
	propertyPanelHeading: {
		background: "#e4ebf4",
		backgroundColor: "#e4ebf4",
		backgroundImage: "none",
		borderBottom: "1px solid #cbd7e5",
		color: "#223044",
		padding: 0
	},
	propertyPanelNestedHeading: {
		background: "#f5f8fb",
		backgroundColor: "#f5f8fb",
		backgroundImage: "none",
		borderBottom: "1px solid #d8e2ee",
		color: "#334155"
	},
	propertyPanelPlainHeader: {
		color: "#223044",
		fontSize: "15px",
		lineHeight: "20px",
		marginLeft: 0,
		marginRight: 0,
		padding: "12px 16px"
	},
	propertyPanelNestedPlainHeader: {
		color: "#334155",
		fontSize: "14px",
		padding: "10px 14px"
	},
	propertyPanelCollapsible: {
		color: "#223044",
		marginLeft: 0,
		marginRight: 0,
		minHeight: "54px",
		padding: "2px 6px",
		alignItems: "center"
	},
	propertyPanelNestedCollapsible: {
		color: "#334155",
		minHeight: "44px",
		padding: "0 4px"
	},
	propertyPanelBody: {
		padding: "12px 14px"
	},
	propertyPanelNestedBody: {
		background: "#ffffff",
		backgroundColor: "#ffffff",
		padding: "10px 12px"
	},
	propertyPanelToggleButton: {
		background: "#ffffff",
		backgroundColor: "#ffffff",
		backgroundImage: "none",
		border: "1px solid #cfd8e3",
		borderRadius: "16px",
		boxShadow: "none",
		color: "#334155",
		minWidth: "32px",
		padding: "4px 8px"
	}
};

export default AmbifiBasicPanel;
